import React, { useCallback, useEffect, useState } from 'react';

import { useCRMHost } from '../CRMHostContext';
import { toUiClaim } from '../adapters';
import { CRM_ACTION } from '../contracts';
import { BulkReviewScreen } from '../../screens/BulkReviewScreen';
import type { BulkFailure } from '../../screens/BulkReviewScreen';
import type { Claim as UiClaim } from '../../screens/mockData';

/** ดึงมาเก็บในเครื่องต่อรอบ — เท่ากับคิวปกติ */
const PAGE_LIMIT = 500;

/**
 * ยิงพร้อมกันได้กี่รายการ
 *
 * ⚠️ backend ไม่มี endpoint อนุมัติเป็นกลุ่ม ต้องยิง PATCH ทีละใบ
 *    ยิงรวดเดียว 200 ใบพร้อมกันคือการยิง DDoS ใส่ระบบตัวเอง และ HIS
 *    ใช้ฐานข้อมูลร่วมกับงานอื่นที่คนไข้ใช้อยู่จริง
 *
 *    6 เป็นค่าที่พอสมควร — ยังเร็วกว่าการยิงทีละใบมาก แต่ไม่ถล่ม connection pool
 *    ถ้าปรับ ต้องวัดกับ staging ก่อน ไม่ใช่เดา
 */
const CONCURRENCY = 6;

/**
 * ตัวต่อของหน้าอนุมัติแบบกลุ่ม
 *
 * ต่างจากคิวปกติตรงที่ต้องยิงหลายรายการและ **ล้มเหลวบางส่วนได้**
 * จึงต้องรายงานกลับว่าใบไหนไม่สำเร็จ ไม่ใช่กลืน error แล้วบอกว่าเสร็จ
 */
export function BulkReviewContainer() {
  const { api, dataMode, refreshKey } = useCRMHost();
  const isMock = dataMode === 'mock';

  const [claims, setClaims] = useState<UiClaim[] | undefined>(undefined);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [rewardMultiplier, setRewardMultiplier] = useState<number | null>(null);

  const load = useCallback(() => {
    if (isMock) { setState('ready'); return; }
    let cancelled = false;
    setState('loading');

    Promise.all([
      api.listClaims({ limit: PAGE_LIMIT, ordering: 'created' }),
      /* อัตราแลกเปลี่ยนคะแนน — ถ้าไม่ได้ ตัวเลขคะแนนที่โชว์จะผิด จึงไม่ปล่อยผ่านเงียบ */
      api.listEarningPolicies(),
    ])
      .then(([page, policies]) => {
        if (cancelled) return;
        const active = policies.items.find((p) => p.default) ?? policies.items[0];
        setRewardMultiplier(active ? Number(active.rewardMultiplier) : null);
        setClaims(page.items.map(toUiClaim));
        setState('ready');
      })
      .catch(() => { if (!cancelled) setState('error'); });

    return () => { cancelled = true; };
  }, [isMock, api]);

  useEffect(() => load(), [load, refreshKey]);

  /**
   * อนุมัติทีละใบแบบจำกัดจำนวนที่ยิงพร้อมกัน
   *
   * ⚠️ **ต้องส่ง `amount` ไปด้วยทุกครั้ง** ไม่งั้นได้ 400 "Amount is required"
   *    ใช้ยอดที่เจ้าหน้าที่แก้ถ้ามี ไม่งั้นใช้ยอดเดิมของคำขอ
   *
   * เก็บความล้มเหลวรายใบแล้วคืนกลับ ไม่ throw ทั้งชุด เพราะรายการที่สำเร็จ
   * ถูกอนุมัติจริงที่ backend ไปแล้ว การ throw จะทำให้หน้าจอคืนค่าทั้งชุดผิดความจริง
   */
  const onBulkApprove = useCallback(
    async (batch: UiClaim[], reason: string): Promise<BulkFailure[]> => {
      if (isMock) return [];

      const failures: BulkFailure[] = [];
      const queue = [...batch];

      const worker = async () => {
        for (;;) {
          const claim = queue.shift();
          if (!claim) return;

          const amount = claim.verifiedAmount ?? claim.declaredAmount;
          try {
            await api.actOnClaim(Number(claim.id), CRM_ACTION.APPROVE, {
              amount,
              /* เหตุผลที่กรอกในกล่องยืนยัน ไปอยู่ในประวัติการตรวจของทุกรายการ
                 และใช้เป็นเหตุผลของการแก้ยอดด้วย ถ้ารายการนั้นถูกแก้ยอดไว้ (backend บังคับ) */
              comment: reason,
            });
          } catch (err) {
            failures.push({
              claimId: claim.id,
              claimCode: claim.code,
              message: err instanceof Error ? err.message : 'ไม่ทราบสาเหตุ',
            });
          }
        }
      };

      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, batch.length) }, worker));

      /* โหลดใหม่เพื่อให้สถานะตรงกับ backend จริง — รวมรายการที่คนอื่นตัดสินระหว่างนี้ */
      load();
      return failures;
    },
    [isMock, api, load],
  );

  if (isMock) return <BulkReviewScreen initialState="ready" />;

  return (
    <BulkReviewScreen
      initialState={state}
      claims={claims ?? []}
      onBulkApprove={onBulkApprove}
      onRefresh={load}
      refreshing={state === 'loading'}
      pointsForAmount={
        rewardMultiplier === null ? undefined : (amount) => Math.floor(amount * rewardMultiplier)
      }
    />
  );
}
